import { OppButton } from '../ui/control';
import { buttonVariants } from '../ui/controlStyles';
import { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useSharedAuth, useSharedProfile } from '@litopia/shared';
import { useCopy } from '../../copy';
import { useOppNotifications } from '../../contexts/OppNotificationContext';
import { getInitial } from '../../utils/userDisplay';
import { getSharedProfileAvatar, getSharedProfileDisplayName } from '../../utils/sharedProfileDisplay';
import { OPP_TOP_NAVIGATION } from '../../config/primaryNavigation';
import { OpenPenPalMark } from '../semantic/OpenPenPalMark';
import {
  Bell,
  Menu,
  X,
  ChevronDown,
  User,
  Settings,
  LogOut,
  PenLine,
  Sparkles,
} from 'lucide-react';

export default function Header() {
  const { user, signOut } = useSharedAuth();
  const { t } = useCopy();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [avatarOpen, setAvatarOpen] = useState(false);
  const { unreadCount } = useOppNotifications();
  const { data: sharedProfileData } = useSharedProfile(user?.id);

  const displayName = getSharedProfileDisplayName(sharedProfileData, user?.email);
  const avatarUrl = getSharedProfileAvatar(sharedProfileData);
  const initial = getInitial(displayName);

  const NAV_LINKS = OPP_TOP_NAVIGATION.map((item) => ({
    ...item,
    label: t(item.key),
  }));

  return (
    <>
      <header className="opp-shell-header sticky top-0 z-40 bg-[hsl(var(--opp-nav)/.82)] backdrop-blur-xl">
        <div className="mx-auto flex min-h-16 w-full min-w-0 max-w-[var(--litopia-layout-1400px)] items-center justify-between px-4 sm:px-6 md:px-8 lg:px-12">
          <OpenPenPalMark className="min-w-0 shrink" />

          {/* Center: Desktop nav */}
          <nav aria-label="OpenPenPal 顶部导航" className="hidden items-center gap-0.5 md:flex">
            {NAV_LINKS.map((link) => {
              const isActive =
                link.href === '/'
                  ? pathname === '/'
                  : pathname.startsWith(link.href);
              return (
                <Link
                  key={link.href}
                  to={link.href}
                  aria-current={isActive ? 'page' : undefined}
                  className={`inline-flex min-h-11 touch-manipulation items-center rounded-xl px-3.5 py-2 text-sm transition-all ${
                    isActive
                      ? 'bg-[hsl(var(--opp-accent-soft))] font-semibold text-[hsl(var(--opp-ink))]'
                      : 'font-medium text-[hsl(var(--opp-muted))] hover:bg-[hsl(var(--opp-surface-raised))] hover:text-[hsl(var(--opp-ink))]'
                  }`}
                >
                  {link.label}
                </Link>
              );
            })}
          </nav>

          {/* Right: Actions */}
          <div className="flex shrink-0 items-center gap-0.5 sm:gap-1.5">
            {/* Write CTA (desktop only) */}
            {user && (
              <Link
                to="/write"
                className={buttonVariants({ variant: 'primary', className: 'hidden md:inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-sm font-semibold shadow-sm' })}
              >
                <PenLine className="h-3.5 w-3.5" />
                {t('nav.write')}
              </Link>
            )}

            {/* AI is a tool action, not part of the product navigation. */}
            <Link to="/ai" aria-label="AI" className="hidden min-h-11 min-w-11 items-center justify-center p-2 text-[hsl(var(--opp-accent-strong))] hover:text-[hsl(var(--opp-ink))] transition-colors rounded-lg hover:bg-[hsl(var(--opp-accent-soft))] md:inline-flex" title="AI">
              <Sparkles className="w-5 h-5" />
            </Link>

            {user ? (
              <>
                {/* Bell */}
                <Link to="/notifications" aria-label="通知" className="relative flex min-h-11 min-w-11 items-center justify-center rounded-lg p-2 text-[hsl(var(--opp-accent-strong))] hover:bg-[hsl(var(--opp-accent-soft))] transition-colors active:scale-95">
                  <Bell className="h-5 w-5" />
                  {unreadCount > 0 && (
                    <span className="absolute -top-0.5 -right-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-[hsl(var(--opp-danger-fg))] px-1 text-caption font-bold text-[hsl(var(--opp-danger-bg))] ring-2 ring-[hsl(var(--opp-surface-raised))]">
                      {unreadCount > 99 ? '99+' : unreadCount}
                    </span>
                  )}
                </Link>

                {/* Avatar dropdown */}
                <div className="relative">
                  <OppButton
                    onClick={() => setAvatarOpen(!avatarOpen)}
                    className="flex min-h-11 min-w-11 items-center justify-center gap-1.5 rounded-xl border border-[hsl(var(--opp-border))] bg-[hsl(var(--opp-surface-raised))] px-2.5 py-1.5 hover:bg-[hsl(var(--opp-accent-soft))] transition-colors"
                  >
                    <div className="flex h-6 w-6 items-center justify-center rounded-full bg-[hsl(var(--opp-accent))] text-[hsl(var(--opp-accent-foreground))] overflow-hidden">
                      {avatarUrl ? (
                        <img src={avatarUrl} alt={displayName} className="h-full w-full object-cover" />
                      ) : (
                        <span className="font-serif text-xs font-bold">{initial}</span>
                      )}
                    </div>
                    <span className="hidden max-w-20 truncate text-xs font-semibold text-[hsl(var(--opp-ink))] sm:block">
                      {displayName}
                    </span>
                    <ChevronDown className="h-3.5 w-3.5 text-[hsl(var(--opp-muted))]" />
                  </OppButton>

                  {avatarOpen && (
                    <>
                      {/* Backdrop */}
                      <OppButton
                        type="button"
                        aria-label="关闭个人菜单"
                        className="fixed inset-0 z-10"
                        onClick={() => setAvatarOpen(false)}
                      />
                      {/* Dropdown */}
                      <div className="absolute right-0 top-full z-20 mt-2 w-48 overflow-hidden rounded-xl border border-[hsl(var(--opp-border))] bg-[hsl(var(--opp-surface-raised))] shadow-xl">
                        <div className="border-b border-[hsl(var(--opp-border))] px-4 py-3">
                          <p className="text-xs font-semibold text-[hsl(var(--opp-ink))]">{displayName}</p>
                          <p className="truncate text-xs text-[hsl(var(--opp-muted))]">{user.email}</p>
                        </div>
                        <Link
                          to="/me"
                          onClick={() => setAvatarOpen(false)}
                          className="flex items-center gap-2.5 px-4 py-2.5 text-sm text-[hsl(var(--opp-ink))] hover:bg-[hsl(var(--opp-surface-raised))] transition-colors"
                        >
                          <User className="h-4 w-4 text-[hsl(var(--opp-muted))]" />
                          {t('nav.profile')}
                        </Link>
                        <Link
                          to="/settings"
                          onClick={() => setAvatarOpen(false)}
                          className="flex items-center gap-2.5 px-4 py-2.5 text-sm text-[hsl(var(--opp-ink))] hover:bg-[hsl(var(--opp-surface-raised))] transition-colors"
                        >
                          <Settings className="h-4 w-4 text-[hsl(var(--opp-muted))]" />
                          {t('nav.settings')}
                        </Link>
                        <div className="border-t border-[hsl(var(--opp-border))]">
                          <OppButton
                            onClick={async () => {
                              setAvatarOpen(false);
                              await signOut();
                              navigate('/login');
                            }}
                            className="flex w-full items-center gap-2.5 px-4 py-2.5 text-sm text-[hsl(var(--opp-danger-fg))] hover:bg-[hsl(var(--opp-danger-bg))] transition-colors"
                          >
                            <LogOut className="h-4 w-4" />
                            {t('common.signout')}
                          </OppButton>
                        </div>
                      </div>
                    </>
                  )}
                </div>
              </>
            ) : (
              <Link
                to="/login"
                className={buttonVariants({ variant: 'primary', className: 'rounded-full px-4 py-1.5 text-sm font-semibold' })}
              >
                {t('common.login')}
              </Link>
            )}

            <OppButton
              aria-label={mobileOpen ? '关闭菜单' : '打开菜单'}
              onClick={() => setMobileOpen(!mobileOpen)}
              className="ml-1 min-h-11 min-w-11 rounded-lg p-2 text-[hsl(var(--opp-accent-strong))] hover:bg-[hsl(var(--opp-accent-soft))] transition-colors active:scale-95 md:hidden"
            >
              {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </OppButton>
          </div>
        </div>

        {/* Mobile nav drawer */}
        {mobileOpen && (
          <div className="border-t border-[hsl(var(--opp-border))] bg-letter-paper px-4 py-3 md:hidden">
            <nav className="space-y-0.5">
              {NAV_LINKS.map((link) => {
                const isActive =
                  link.href === '/'
                    ? pathname === '/'
                    : pathname.startsWith(link.href);
                return (
                  <Link
                    key={link.href}
                    to={link.href}
                    aria-current={isActive ? 'page' : undefined}
                    onClick={() => setMobileOpen(false)}
                    className={`flex min-h-11 touch-manipulation items-center rounded-xl px-4 py-2.5 text-sm font-medium transition-colors ${
                      isActive
                        ? 'bg-[hsl(var(--opp-accent-soft))] font-semibold text-[hsl(var(--opp-ink))]'
                        : 'text-[hsl(var(--opp-accent-strong))] hover:bg-[hsl(var(--opp-surface-raised))] hover:text-[hsl(var(--opp-ink))]'
                    }`}
                  >
                    {link.label}
                  </Link>
                );
              })}
            </nav>
          </div>
        )}
      </header>
    </>
  );
}
